import pluralizeFn from 'pluralize';

import { autoscalerModeDisplayName } from '../../../../../../_constants';
import {
  formatCpuResourceLimit,
  formatMemoryResourceLimit,
} from '../../../../../../_formattingUtils';
import { vpaSettingsConstants } from '../../../../../../_woop-settings/_constants/settingsConstants';
import { WorkloadDetailsFormModel } from '../../../../_types';
import { WorkloadVpaFormSettings } from '../../_constants';

const boolStr = (bool: boolean) => (bool ? 'Enabled' : 'Disabled');
const percentStr = (percent: number) => `${percent}%`;
const containerOverrideStr = (items: { isOverridden: boolean }[]) => {
  const overridden = items.filter((v) => v.isOverridden).length;
  return [
    `CONTAINERS/POD: ${items.length}`,
    ...(overridden ? [`OVERRIDDEN: ${overridden}/${items.length}`] : []),
  ];
};
const percentileStr = (percent: number) => `p${percent}`;
const daysHoursStr = ({
  days,
  hours,
}: {
  days: number | null;
  hours: number | null;
}) =>
  `${days ?? '-'} ${pluralizeFn('day', days ?? 0)} ${
    hours ?? '-'
  } ${pluralizeFn('hour', hours ?? 0)}`;

export const settingsRowDescriptions: Record<
  WorkloadVpaFormSettings,
  (prop: { formModel: WorkloadDetailsFormModel }) => string[]
> = {
  optimizedResources: ({
    formModel: {
      verticalRightsizing: { policy },
    },
  }) => [
    `CPU: ${boolStr(policy.optimizedResources.cpu)}`,
    `Memory: ${boolStr(policy.optimizedResources.memory)}`,
  ],
  excludedContainers: ({
    formModel: {
      verticalRightsizing: { policy },
    },
  }) => [
    `Containers: ${
      policy.excludedContainers.enabled
        ? `Excluded(${policy.excludedContainers.containers.length})`
        : 'All'
    }`,
  ],
  customMetrics: ({ formModel }) =>
    containerOverrideStr(formModel.verticalRightsizing.policy.customMetrics),
  autoscaler: ({ formModel }) => [
    autoscalerModeDisplayName[
      formModel.verticalRightsizing.policy.applyType.value
    ],
  ],
  overhead: ({ formModel }) => [
    `CPU: ${percentStr(formModel.verticalRightsizing.policy.overhead.cpu)}`,
    `Memory: ${percentStr(
      formModel.verticalRightsizing.policy.overhead.memory
    )}`,
  ],
  memoryGrowthDetection: ({ formModel }) => [
    boolStr(formModel.verticalRightsizing.policy.memoryGrowthDetection.enabled),
  ],
  stallDetection: ({ formModel }) => [
    `CPU: ${percentStr(
      formModel.verticalRightsizing.policy.stallDetection
        .cpuStallThresholdPercentage
    )}`,
    `Pods: ${percentStr(
      formModel.verticalRightsizing.policy.stallDetection
        .minPressuredPodPercentage
    )}`,
  ],
  recommendationPercentile: ({ formModel }) => [
    `CPU: ${percentileStr(
      formModel.verticalRightsizing.policy.recommendationPercentile.cpu
    )}`,
    `Memory: ${percentileStr(
      formModel.verticalRightsizing.policy.recommendationPercentile.memory
    )}`,
  ],
  workloadResourceLimits: ({ formModel }) => [
    `CPU: ${formatCpuResourceLimit(
      formModel.verticalRightsizing.policy.resourceLimits.cpu
    )}`,
    `Memory: ${formatMemoryResourceLimit(
      formModel.verticalRightsizing.policy.resourceLimits.memory
    )}`,
  ],
  workloadConstraints: ({ formModel }) =>
    containerOverrideStr(
      formModel.verticalRightsizing.policy.workloadConstraints
    ),
  lookbackPeriod: ({ formModel }) => [
    daysHoursStr({
      days: formModel.verticalRightsizing.policy.lookbackPeriod.cpu.days,
      hours: formModel.verticalRightsizing.policy.lookbackPeriod.cpu.hours,
    }),
    daysHoursStr({
      days: formModel.verticalRightsizing.policy.lookbackPeriod.memory.days,
      hours: formModel.verticalRightsizing.policy.lookbackPeriod.memory.hours,
    }),
  ],
  startupMetrics: ({ formModel }) => {
    const { type, min, sec } =
      formModel.verticalRightsizing.policy.startupMetrics;

    if (type === 'no-strategy') {
      return [vpaSettingsConstants.startupMetrics.noneLabel];
    }

    const label =
      type === 'two-phase'
        ? vpaSettingsConstants.startupMetrics.twoPhaseLabel
        : vpaSettingsConstants.startupMetrics.ignoreLabel;
    const time = `${min} min${sec ? ` ${sec} sec` : ''}`;

    return [`${label}: ${time}`];
  },
  hpaConverter: ({ formModel }) => [
    boolStr(formModel.verticalRightsizing.policy.hpaConverter.enabled),
  ],
  predictiveScaling: ({ formModel }) => [
    boolStr(
      formModel.verticalRightsizing.policy.predictiveScaling?.cpu?.enabled ??
        false
    ),
  ],
};
